import { useCallback, useEffect, useState } from 'react';
import { useQuery } from "@tanstack/react-query";

import dayjs from 'dayjs';

import Log from '@/Logger';
import { listObjects } from '@/feature/api-client/s3';

export type S3FileInfo = {
    name: string
    multiDeviceLink?: string
    serialId: string
    deviceDisplayName: string
    lastModified: string
    size?: number
    preview?: any
    download?: any
    csv?: any
    delete?: any
    others?: any
}

/**
 * 指定されたキーに基づいてS3バケットからPDFレコードのリストを取得する関数。
 * @param {string} key - S3バケット内のオブジェクトをフィルタリングするために使用されるプレフィックス。
 * @returns {Promise<S3FileInfo[]>}　PDFファイルに関する詳細を含むS3Recordオブジェクトの配列に解決されるPromise。
 *          リクエストが失敗した場合やオブジェクトが見つからない場合は、空の配列が返される。
 * @throws リクエストが失敗した場合はエラーをログに記録。
 */
export const getS3List = async (key: string): Promise<S3FileInfo[]> => {
    const logger = Log.getLogger();
    try {
        // 指定されたバケット内のオブジェクトリストを取得する
        const res = await listObjects(key);

        //各PDFファイルに関する関連情報（名前、デバイス、最終更新日、サイズ）を‘/’で区切って抽出し、PdfRecord[]に格納する
        const s3List = res.reduce((acc, { Key, LastModified, Size }) => {
            const splited: string[] = (Key ?? '').split('/');
            if (splited.length <= 4) return acc;
            return [...acc, {
                name: Key ?? '',
                serialId: splited[3],
                deviceDisplayName: splited[3], // 初期値としてシリアルIDをセット
                lastModified: dayjs(LastModified?.toLocaleString() ?? '-').format('YYYY/MM/DD HH:mm:ss'),
                size: Size,
            }];
        }, [] as S3FileInfo[]);
        return s3List;
    } catch (err) {
        //エラーログを記録し、空の配列を返す
        logger.api.error('S3', 'Fail to request: listObjects', err);
        return [];
    }
}

async function fetchS3List(s3Key: string): Promise<S3FileInfo[]> {
    const logger = Log.getLogger();
    try {
        logger.api.info('useS3FileList', 'Start to fetchS3List');

        // S3からPDFの一覧を取得する
        const s3List = await getS3List(s3Key);

        if (s3List.length === 0) {
            logger.api.info('useS3FileList', 'No files found for the given key', { key: s3Key });
            return [];
        }

        // S3リストをそのまま設定（デバイス表示名は画面側で処理）
        logger.api.info('useS3FileList', 'Successfully fetched S3 file list');
        return s3List;

    } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.api.error('useS3FileList', 'Failed to fetch S3 file list', error);
        throw error; // rethrow to be caught in reducer
    }
}

/**
 * PDFリストを管理するカスタムフック
 * S3からファイルの一覧を取得し、S3Recordの配列を提供する
 */
export const useS3FileList = (key: string, s3Key: string) => {
    const queryFn = useCallback(() => fetchS3List(s3Key), [s3Key]);
    const { data: s3FileList, isLoading, isFetching, refetch, error } = useQuery({
        queryKey: [key],
        queryFn,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });
    /* フック外部で検索した結果を表示する場合にsetする。not nullの場合、こちらが優先される。 */
    const [externalSearchList, setExternalSearchList] = useState<S3FileInfo[] | null>(null);
    const [list, setList] = useState(s3FileList);

    // 「ファイル内の文字を検索」の検索結果を適用するための条件分岐
    useEffect(() => {
        const selectedList = externalSearchList !== null ? externalSearchList : s3FileList;
        setList(selectedList);
    }, [s3FileList, externalSearchList]);

    return {
        list,
        refetch,
        isLoading,
        isFetching,
        error,
        setExternalSearchList,
    };
};
